import React, { useEffect, useState } from 'react';
import { CreditCard, Fuel, Gift, ShoppingBag, ShoppingCart, Ticket, UtensilsCrossed } from 'lucide-react';
import { Skeleton } from '../../../components/common/SkeletonLoader';
import { useAuth } from '../../../hooks/useAuth';
import {
  giftCardErrorMessage,
  usePartnerGiftCards,
  type PartnerRewards,
  type TenantGiftCard,
} from '../../../hooks/usePartnerGiftCards';
import { formatCentsAud, formatReferralDate } from '../../../utils/partnerReferrals';
import { defaultReferringBrokerId } from '../ReferClientForm';
import { usePartnerPortalContext } from '../portalContext';

/**
 * Rewards: a subscriber's broker redeems the referral fees a partner owes them
 * as a gift card, from inside VALUTTEN. Owners and administrators redeem on
 * behalf of a broker in their scope (the same picker as Refer a client).
 */

const CARD = 'rounded-xl border border-gray-700 bg-gray-800/40 p-4 sm:p-6';
const TITLE = 'text-sm font-semibold text-gray-200';
const BTN_PRIMARY =
  'inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50';
const BTN_GHOST = 'inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-700/60 disabled:opacity-50';

export const DEMO_BANNER = 'Demo mode: gift cards are simulated. No real card is issued until LocalCard is connected.';

const KIND_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  visa: CreditCard,
  grocery: ShoppingCart,
  dining: UtensilsCrossed,
  fuel: Fuel,
  retail: ShoppingBag,
  experience: Ticket,
};
const iconFor = (kind: string | null | undefined) => (kind && KIND_ICONS[kind]) || Gift;

const STATUS: Record<string, { label: string; cls: string }> = {
  requested: { label: 'Requested', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  awaiting_float: { label: 'Awaiting float', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  issuing: { label: 'Issuing', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  issued: { label: 'Issued', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-700/60 text-gray-300 border-gray-600' },
};

const DemoBanner: React.FC = () => (
  <div role="note" className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm font-medium text-amber-200">
    {DEMO_BANNER}
  </div>
);

const PartnerRedeem: React.FC<{
  partner: PartnerRewards;
  recipient: string;
  onRequest: (catalogueKey: string) => Promise<string | null>;
}> = ({ partner, recipient, onRequest }) => {
  const [choice, setChoice] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rewards = partner.rewards;

  if (!rewards || !rewards.enabled) {
    return (
      <div className={CARD}>
        <p className="text-sm text-gray-300">{partner.partnerName} has not switched on gift cards yet.</p>
      </div>
    );
  }

  const amount = partner.available?.amountIncGstCents ?? 0;
  const chosen = rewards.catalogue.find((c) => c.key === choice) ?? null;

  const confirm = async () => {
    if (!chosen) return;
    setBusy(true);
    setError(null);
    const err = await onRequest(chosen.key);
    setBusy(false);
    if (err) setError(err);
    else {
      setConfirming(false);
      setChoice(null);
    }
  };

  return (
    <div className="space-y-4" data-partner={partner.partnerId}>
      {rewards.mode === 'demo' && <DemoBanner />}
      <div className="rounded-xl border border-blue-500/30 bg-gradient-to-br from-blue-600/25 via-gray-800/60 to-gray-900 p-6">
        <p className="text-xs font-semibold uppercase tracking-wider text-blue-200/80">Available from {partner.partnerName}</p>
        <p className="mt-1 text-3xl font-semibold text-white" data-testid={`available-${partner.partnerId}`}>
          {formatCentsAud(amount)}
        </p>
        <p className="mt-1 text-sm text-gray-300">
          {amount > 0
            ? `${partner.available?.entryCount ?? 0} referral ${partner.available?.entryCount === 1 ? 'fee' : 'fees'} owed to you, GST inclusive`
            : 'Nothing owed right now'}
        </p>
      </div>

      <div className={`${CARD} space-y-4`}>
        <h2 className={TITLE}>Choose a gift card</h2>
        {rewards.catalogue.length === 0 ? (
          <p className="text-sm text-gray-400">No card types are set up yet.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" role="group" aria-label="Gift card types">
            {rewards.catalogue.map((c) => {
              const Icon = iconFor(c.kind);
              const on = choice === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => {
                    setChoice(c.key);
                    setConfirming(false);
                  }}
                  className={`flex flex-col items-start gap-3 rounded-lg border p-4 text-left transition-colors ${
                    on ? 'border-blue-400 bg-blue-500/15 ring-1 ring-blue-400' : 'border-gray-700 bg-gray-900/40 hover:border-gray-500'
                  }`}
                >
                  <Icon className="h-5 w-5 text-blue-300" />
                  <span className="text-sm font-medium text-gray-100">{c.label}</span>
                  {c.expiryDays ? <span className="text-xs text-gray-400">Spend within {c.expiryDays} days</span> : null}
                </button>
              );
            })}
          </div>
        )}

        {!confirming ? (
          <div>
            <button type="button" className={BTN_PRIMARY} disabled={!chosen || amount <= 0} onClick={() => setConfirming(true)}>
              Redeem as gift card
            </button>
            {amount <= 0 && <p className="mt-2 text-xs text-gray-400">Nothing owed right now.</p>}
          </div>
        ) : (
          chosen && (
            <div role="alertdialog" aria-labelledby={`gc-confirm-${partner.partnerId}`} className="rounded-lg border border-gray-600 bg-gray-900/60 p-4">
              <p id={`gc-confirm-${partner.partnerId}`} className="text-sm font-medium text-gray-100">
                Send a {formatCentsAud(amount)} {chosen.label} card to {recipient}?
                {chosen.expiryDays ? ` It must be spent within ${chosen.expiryDays} days of being sent; anything unspent then goes back to ${partner.partnerName}.` : ''}
              </p>
              <div className="mt-3 flex gap-2">
                <button type="button" className={BTN_PRIMARY} onClick={() => void confirm()} disabled={busy}>
                  {busy ? 'Sending…' : 'Confirm'}
                </button>
                <button type="button" className={BTN_GHOST} onClick={() => setConfirming(false)} disabled={busy}>
                  Back
                </button>
              </div>
            </div>
          )
        )}
        {error && (
          <p className="text-sm text-red-300" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

const RewardsTab: React.FC = () => {
  const { brokers, hideFees, loading: scopeLoading } = usePartnerPortalContext();
  const { user } = useAuth();
  const [brokerDocId, setBrokerDocId] = useState<string>(() => defaultReferringBrokerId(brokers ?? []));
  // Brokers load asynchronously; preselect the caller (or the only broker) once they arrive.
  useEffect(() => {
    if (!brokerDocId && brokers?.length) setBrokerDocId(defaultReferringBrokerId(brokers));
  }, [brokers, brokerDocId]);
  const pickerNeeded = (brokers?.length ?? 0) > 1;
  const selected = brokers?.find((b) => b.brokerDocId === brokerDocId) ?? null;
  // The server covers the caller's own broker when brokerDocId is omitted; any other broker must be named.
  const target = brokerDocId && !selected?.isSelf ? brokerDocId : null;
  const { data, loading, error, reload, request, cancel } = usePartnerGiftCards(target, !scopeLoading && !(pickerNeeded && !brokerDocId));
  const [notice, setNotice] = useState<string | null>(null);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  if (hideFees) {
    return <div className={CARD}><p className="text-sm text-gray-400">Referral fees are hidden for your role.</p></div>;
  }

  const recipient = !pickerNeeded || selected?.isSelf ? user?.email || 'your VALUTTEN email' : `${selected?.name ?? 'the broker'}'s VALUTTEN email`;

  const onRequest = async (partner: PartnerRewards, catalogueKey: string): Promise<string | null> => {
    setNotice(null);
    try {
      const card = await request({ partnerId: partner.partnerId, catalogueKey });
      setNotice(`Requested a ${formatCentsAud(card?.valueCents ?? 0)} gift card. ${partner.partnerName} will issue it.`);
      await reload();
      return null;
    } catch (err) {
      return giftCardErrorMessage(err);
    }
  };

  const doCancel = async (g: TenantGiftCard) => {
    setCancelBusy(true);
    setCancelError(null);
    try {
      await cancel(g.giftCardId);
      setCancelId(null);
      setNotice('Request cancelled. The fees are available again.');
      await reload();
    } catch (err) {
      setCancelError(giftCardErrorMessage(err));
    } finally {
      setCancelBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {pickerNeeded && (
        <div className={CARD}>
          <label htmlFor="gc-broker" className="mb-1 block text-sm font-medium text-gray-300">
            Redeem for
          </label>
          <select
            id="gc-broker"
            value={brokerDocId}
            onChange={(e) => {
              setBrokerDocId(e.target.value);
              setNotice(null);
            }}
            className="w-full max-w-sm rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100"
          >
            <option value="">Choose a broker</option>
            {brokers.map((b) => (
              <option key={b.brokerDocId} value={b.brokerDocId}>
                {b.isSelf ? `${b.name} (you)` : b.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {notice && (
        <div role="status" className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
          {notice}
        </div>
      )}

      {pickerNeeded && !brokerDocId ? (
        <div className={CARD}><p className="text-sm text-gray-400">Choose a broker to see what they are owed.</p></div>
      ) : loading && !data ? (
        <div className={`${CARD} space-y-3`}>
          <Skeleton height="2rem" width="12rem" />
          <Skeleton height="6rem" />
        </div>
      ) : error && !data ? (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" role="alert">
          Could not load gift cards. {error}
        </div>
      ) : (
        <>
          {(data?.partners ?? []).length === 0 ? (
            <div className={CARD}><p className="text-sm text-gray-400">No referral partner offers gift cards yet.</p></div>
          ) : (
            (data?.partners ?? []).map((p) => (
              <PartnerRedeem key={p.partnerId} partner={p} recipient={recipient} onRequest={(key) => onRequest(p, key)} />
            ))
          )}

          <div className={CARD}>
            <h2 className={`${TITLE} mb-3`}>Your gift cards</h2>
            {(data?.giftCards ?? []).length === 0 ? (
              <p className="text-sm text-gray-400">No gift cards yet.</p>
            ) : (
              <ul className="divide-y divide-gray-700">
                {(data?.giftCards ?? []).map((g) => {
                  const held = g.status === 'requested' && g.holdReason === 'awaiting_float';
                  const st = (held ? STATUS.awaiting_float : STATUS[g.status]) ?? { label: g.status, cls: 'bg-gray-700/60 text-gray-300 border-gray-600' };
                  return (
                    <li key={g.giftCardId} data-giftcard={g.giftCardId} className="py-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-gray-100">
                            {g.catalogueLabel || 'Gift card'}
                            {pickerNeeded && (g.memberName || g.brokerName) ? <span className="text-gray-400"> · {g.memberName || g.brokerName}</span> : null}
                          </p>
                          <p className="text-xs text-gray-400">
                            {g.status === 'issued'
                              ? `Ref ${g.reference ?? '-'} · issued ${formatReferralDate(g.issuedAt)}`
                              : `Requested ${formatReferralDate(g.requestedAt)}`}
                            {g.mode === 'demo' ? ' · demo' : ''}
                          </p>
                          {held && (
                            <p className="text-xs text-amber-200/90">The partner is topping up its gift card float. Your fees stay reserved and the card is sent once it is funded.</p>
                          )}
                        </div>
                        <span className="text-sm font-medium tabular-nums text-gray-100">{formatCentsAud(g.valueCents)}</span>
                        <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span>
                        {g.status === 'requested' && (
                          <button type="button" className={BTN_GHOST} onClick={() => setCancelId(g.giftCardId)} disabled={cancelId === g.giftCardId}>
                            Cancel
                          </button>
                        )}
                      </div>
                      {cancelId === g.giftCardId && (
                        <div role="alertdialog" aria-labelledby={`gc-cancel-${g.giftCardId}`} className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                          <p id={`gc-cancel-${g.giftCardId}`} className="text-sm text-amber-100">
                            Cancel this request? The fees become available again.
                          </p>
                          <div className="mt-2 flex gap-2">
                            <button type="button" className={BTN_PRIMARY} onClick={() => void doCancel(g)} disabled={cancelBusy}>
                              Yes, cancel request
                            </button>
                            <button type="button" className={BTN_GHOST} onClick={() => setCancelId(null)}>
                              Keep request
                            </button>
                          </div>
                          {cancelError && <p className="mt-2 text-sm text-red-300">{cancelError}</p>}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default RewardsTab;
